// src/components/home/MyNotesCard.js
//
// "My notes" on the home page (UI revamp): tabs Today / This week (short goals
// you add, tick and delete) and Notes (one free-text box). Private to the
// person; saved by useHomePrivate. Goals stay until deleted: nothing resets at
// midnight or at the start of a week.

import { useState } from "react";
import { Plus, X } from "lucide-react";
import { LIMITS, addGoal, toggleGoal, deleteGoal, sortGoals, openCount } from "../../services/homePrivate";
import SaveStatus from "./SaveStatus";

const TABS = [
  { id: "today", label: "Today" },
  { id: "week", label: "This week" },
  { id: "notes", label: "Notes" },
];

const fmtAdded = (ms) => new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric" });

const GoalList = ({ list, onChange, listLabel }) => {
  const [text, setText] = useState("");
  const [error, setError] = useState("");
  const add = (e) => {
    e.preventDefault();
    const r = addGoal(list, text);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    setError("");
    setText("");
    onChange(r.list);
  };
  const sorted = sortGoals(list);
  return (
    <div>
      <form onSubmit={add} className="flex gap-2">
        <label className="sr-only" htmlFor={`rx-goal-${listLabel}`}>
          Add a goal for {listLabel}
        </label>
        <input
          id={`rx-goal-${listLabel}`}
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={LIMITS.itemText}
          placeholder={`Add a goal for ${listLabel.toLowerCase()}`}
          className="rx-home-input flex-1 min-w-0"
        />
        <button type="submit" className="rx-icon-button rx-home-add" aria-label={`Add goal to ${listLabel}`} title="Add">
          <Plus className="w-4 h-4" />
        </button>
      </form>
      {error && <p className="rx-home-warn text-xs mt-1.5">{error}</p>}
      {sorted.length === 0 ? (
        <p className="rx-home-muted text-sm mt-3">Nothing here yet.</p>
      ) : (
        <ul className="rx-home-list mt-2">
          {sorted.map((g) => (
            <li key={g.id} className="rx-home-row">
              <input
                type="checkbox"
                id={`rx-g-${g.id}`}
                checked={g.done}
                onChange={() => onChange(toggleGoal(list, g.id))}
                className="rx-home-check"
              />
              <label htmlFor={`rx-g-${g.id}`} className={`text-sm flex-1 min-w-0 break-words ${g.done ? "rx-goal-done" : ""}`}>
                {g.text}
              </label>
              <span className="rx-home-muted text-[11px] shrink-0">{fmtAdded(g.addedAt)}</span>
              <button
                type="button"
                className="rx-icon-button shrink-0"
                aria-label={`Delete ${g.text}`}
                title="Delete"
                onClick={() => onChange(deleteGoal(list, g.id))}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default function MyNotesCard({ store }) {
  const [tab, setTab] = useState("today");
  const { data, loaded, loadError, status, errorText, update } = store;
  const counts = { today: openCount(data.today), week: openCount(data.week) };

  return (
    <section className="rx-home-card" aria-label="My notes">
      <div className="flex items-baseline justify-between gap-3 mb-3">
        <h2 className="rx-home-title">My notes</h2>
        <SaveStatus status={status} errorText={errorText} />
      </div>
      {loadError ? (
        <p className="rx-home-muted text-sm">{loadError}</p>
      ) : !loaded ? (
        <p className="rx-home-muted text-sm">Loading…</p>
      ) : (
        <>
          <div role="tablist" aria-label="My notes" className="rx-home-tabs mb-3">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                id={`rx-notes-tab-${t.id}`}
                aria-selected={tab === t.id}
                aria-controls={`rx-notes-panel-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`rx-home-tab ${tab === t.id ? "rx-home-tab--on" : ""}`}
              >
                {t.label}
                {t.id !== "notes" && counts[t.id] > 0 && <span className="rx-home-tab-count">{counts[t.id]}</span>}
              </button>
            ))}
          </div>
          <div role="tabpanel" id={`rx-notes-panel-${tab}`} aria-labelledby={`rx-notes-tab-${tab}`}>
            {tab === "today" && (
              <GoalList list={data.today} listLabel="Today" onChange={(l) => update((d) => ({ ...d, today: l }))} />
            )}
            {tab === "week" && (
              <GoalList list={data.week} listLabel="This week" onChange={(l) => update((d) => ({ ...d, week: l }))} />
            )}
            {tab === "notes" && (
              <div>
                <label htmlFor="rx-notes-text" className="sr-only">
                  Notes
                </label>
                <textarea
                  id="rx-notes-text"
                  value={data.notes}
                  maxLength={LIMITS.notes}
                  rows={6}
                  onChange={(e) => {
                    const v = e.target.value.slice(0, LIMITS.notes);
                    update((d) => ({ ...d, notes: v }));
                  }}
                  className="rx-home-input w-full resize-y"
                  placeholder="Anything you want to remember"
                />
                <p className="rx-home-muted text-[11px] text-right mt-1 tabular-nums" aria-live="polite">
                  {data.notes.length} / {LIMITS.notes}
                </p>
              </div>
            )}
          </div>
        </>
      )}
      <p className="rx-home-muted text-[11px] mt-3">Private to you. Don't enter patient information.</p>
    </section>
  );
}
